// WEQASA Internal Chat Component
// Chat between seller and assigned agent after mandate signature

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  MessageSquare, Send, Paperclip, X, Check, CheckCheck, File, Image as ImageIcon,
  FileText, Download, Clock, User, Building, AlertCircle, Loader2, ChevronDown,
  Play, Pause, Mic, Square, Smile
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

// Document classification dialog for agent
const DocumentClassifyDialog = ({ open, onOpenChange, messageId, fileName, onClassify }) => {
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch document categories
    const fetchCategories = async () => {
      try {
        const response = await axios.get('/chat/document-categories');
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    if (open) fetchCategories();
  }, [open]);

  const handleClassify = async () => {
    if (!category) return;
    setLoading(true);
    try {
      await axios.put(`/chat/document/${messageId}/classify`, {
        document_category: category
      });
      toast.success('Documento classificato con successo');
      onClassify(category);
      onOpenChange(false);
    } catch (error) {
      toast.error('Errore nella classificazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Classifica Documento</DialogTitle>
          <DialogDescription>
            Assegna una categoria al documento "{fileName}" per associarlo alla documentazione dell'immobile.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Seleziona categoria..." />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button onClick={handleClassify} disabled={!category || loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Classifica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Single message component
const ChatMessage = ({ message, isOwn, userType, onClassify, onReaction, currentUserId }) => {
  const isSystem = message.is_system_message;
  const isFile = message.message_type === 'file';
  const isVoice = message.message_type === 'voice' || message.is_voice_message;
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👏', '🎉'];

  const formatTime = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'HH:mm', { locale: it });
    } catch {
      return '';
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <ImageIcon className="h-5 w-5" />;
    if (mimeType?.includes('pdf')) return <FileText className="h-5 w-5" />;
    return <File className="h-5 w-5" />;
  };

  const handleReaction = (emoji) => {
    onReaction(message.message_id, emoji);
    setShowReactions(false);
  };

  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Get reactions summary
  const reactions = message.reactions || {};
  const reactionCounts = {};
  Object.values(reactions).forEach(emoji => {
    reactionCounts[emoji] = (reactionCounts[emoji] || 0) + 1;
  });
  const hasReactions = Object.keys(reactionCounts).length > 0;
  const myReaction = reactions[currentUserId];

  // System message
  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-[#F5F5F7] text-[#86868B] text-[12px] px-4 py-2 rounded-full max-w-[80%] text-center">
          {message.text_content}
        </div>
      </div>
    );
  }

  // Regular message
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3 group`}>
      <div className={`max-w-[75%] relative ${isOwn ? 'order-2' : 'order-1'}`}>
        {/* Sender name for received messages */}
        {!isOwn && (
          <div className="text-[11px] text-[#86868B] mb-1 ml-3 font-medium">
            {message.sender_name}
          </div>
        )}
        
        <div className={`rounded-2xl px-4 py-2.5 relative ${
          isOwn 
            ? 'bg-[#1D1D1F] text-white rounded-br-sm' 
            : 'bg-[#F5F5F7] text-[#1D1D1F] rounded-bl-sm'
        }`}>
          {/* Voice message */}
          {isVoice && (
            <div className="flex items-center space-x-3 min-w-[200px]">
              <button 
                onClick={toggleAudio}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  isOwn ? 'bg-white/20 hover:bg-white/30' : 'bg-[#1D1D1F]/10 hover:bg-[#1D1D1F]/20'
                }`}
              >
                {isPlaying ? (
                  <Pause className={`h-5 w-5 ${isOwn ? 'text-white' : 'text-[#1D1D1F]'}`} />
                ) : (
                  <Play className={`h-5 w-5 ${isOwn ? 'text-white' : 'text-[#1D1D1F]'}`} fill="currentColor" />
                )}
              </button>
              <div className="flex-1">
                <div className={`h-1 rounded-full ${isOwn ? 'bg-white/30' : 'bg-[#1D1D1F]/20'}`}>
                  <div className={`h-full rounded-full w-0 ${isOwn ? 'bg-white' : 'bg-[#1D1D1F]'}`} 
                       style={{width: isPlaying ? '100%' : '0%', transition: 'width 0.1s'}} />
                </div>
                <div className={`text-[11px] mt-1 ${isOwn ? 'text-white/60' : 'text-[#86868B]'}`}>
                  Messaggio vocale
                </div>
              </div>
              <audio 
                ref={audioRef} 
                src={message.attachment_url}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
            </div>
          )}
          
          {/* File attachment */}
          {isFile && !isVoice && (
            <div className={`flex items-center space-x-3 p-2 rounded-xl mb-2 ${
              isOwn ? 'bg-white/10' : 'bg-white'
            }`}>
              <div className={`p-2 rounded-xl ${isOwn ? 'bg-white/20' : 'bg-[#F5F5F7]'}`}>
                {getFileIcon(message.attachment_mime_type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-medium truncate ${isOwn ? 'text-white' : 'text-[#1D1D1F]'}`}>
                  {message.attachment_file_name}
                </p>
                <a 
                  href={message.attachment_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`text-[11px] ${isOwn ? 'text-white/70 hover:text-white' : 'text-[#0A2540] hover:underline'}`}
                >
                  <Download className="h-3 w-3 inline mr-1" />
                  Scarica
                </a>
              </div>
            </div>
          )}
          
          {/* Text content */}
          {message.text_content && !isVoice && (
            <p className="text-[14px] whitespace-pre-wrap break-words">
              {message.text_content}
            </p>
          )}
          
          {/* Timestamp and read status */}
          <div className={`flex items-center justify-end space-x-1 mt-1 ${
            isOwn ? 'text-white/50' : 'text-[#86868B]'
          }`}>
            <span className="text-[10px]">{formatTime(message.created_at)}</span>
            {isOwn && (
              message.is_read 
                ? <CheckCheck className="h-3 w-3" />
                : <Check className="h-3 w-3" />
            )}
          </div>
          
          {/* Reactions display */}
          {hasReactions && (
            <div className={`flex flex-wrap gap-1 mt-2 -mb-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
              {Object.entries(reactionCounts).map(([emoji, count]) => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(emoji)}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] transition-colors ${
                    myReaction === emoji 
                      ? 'bg-[#0071e3]/20 border border-[#0071e3]' 
                      : isOwn ? 'bg-white/10' : 'bg-white border border-black/5'
                  }`}
                >
                  <span>{emoji}</span>
                  {count > 1 && <span className="ml-1 text-[10px]">{count}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Reaction button - appears on hover */}
        <button
          onClick={() => setShowReactions(!showReactions)}
          className={`opacity-0 group-hover:opacity-100 absolute top-0 ${isOwn ? 'left-0 -translate-x-full mr-2' : 'right-0 translate-x-full ml-2'} p-1.5 rounded-full bg-white shadow-md border border-black/5 transition-opacity`}
        >
          <Smile className="h-4 w-4 text-[#86868B]" />
        </button>
        
        {/* Reactions picker */}
        {showReactions && (
          <div className={`absolute top-0 ${isOwn ? 'left-0 -translate-x-full' : 'right-0 translate-x-full'} bg-white rounded-2xl shadow-lg border border-black/5 p-2 flex gap-1 z-10`}>
            {REACTIONS.map(emoji => (
              <button
                key={emoji}
                onClick={() => handleReaction(emoji)}
                className={`w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F5F7] transition-colors text-lg ${myReaction === emoji ? 'bg-[#0071e3]/20' : ''}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        
        {/* Classify button for agent on received files */}
        {isFile && !isOwn && userType === 'agent' && !isVoice && (
          <button 
            onClick={() => setClassifyOpen(true)}
            className="text-[12px] text-[#0A2540] hover:underline mt-1 ml-3 font-medium"
          >
            Classifica documento
          </button>
        )}
      </div>
      
      <DocumentClassifyDialog 
        open={classifyOpen}
        onOpenChange={setClassifyOpen}
        messageId={message.message_id}
        fileName={message.attachment_file_name}
        onClassify={onClassify}
      />
    </div>
  );
};

// Date separator
const DateSeparator = ({ date }) => {
  const formatDate = (dateStr) => {
    try {
      const d = parseISO(dateStr);
      if (isToday(d)) return 'Oggi';
      if (isYesterday(d)) return 'Ieri';
      return format(d, 'EEEE d MMMM', { locale: it });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex items-center my-4">
      <div className="flex-1 border-t border-gray-200" />
      <span className="px-3 text-xs text-gray-500 font-medium">
        {formatDate(date)}
      </span>
      <div className="flex-1 border-t border-gray-200" />
    </div>
  );
};

// Main Chat Component
const PropertyChat = ({ 
  propertyId, 
  chatId: initialChatId, 
  userType,
  otherPartyName,
  compact = false 
}) => {
  const { token, user } = useAuth();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [otherTypingName, setOtherTypingName] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Handle reaction
  const handleReaction = async (messageId, emoji) => {
    try {
      // Check if user already has this reaction
      const message = messages.find(m => m.message_id === messageId);
      const existingReaction = message?.reactions?.[user?.user_id];
      
      if (existingReaction === emoji) {
        // Remove reaction
        await axios.delete(`/chat/message/${messageId}/reaction`);
      } else {
        // Add/update reaction
        await axios.post(`/chat/message/${messageId}/reaction`, { emoji });
      }
    } catch (err) {
      toast.error('Errore nell\'aggiunta della reazione');
    }
  };

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Send voice message
        await sendVoiceMessage(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Update recording time
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);

    } catch (err) {
      toast.error('Impossibile accedere al microfono');
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  // Cancel voice recording
  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      audioChunksRef.current = [];
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      // Stop audio tracks
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };

  // Send voice message
  const sendVoiceMessage = async (audioBlob) => {
    if (!chat?.chat_id) return;
    
    try {
      setSending(true);
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice_message.webm');
      
      await axios.post(`/chat/${chat.chat_id}/voice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Messaggio vocale inviato');
    } catch (err) {
      toast.error('Errore nell\'invio del messaggio vocale');
    } finally {
      setSending(false);
    }
  };

  // Format recording time
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch chat and messages
  const fetchChat = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get chat for property
      const chatRes = await axios.get(`/chat/property/${propertyId}`);
      setChat(chatRes.data);

      // Get messages
      const msgRes = await axios.get(`/chat/${chatRes.data.chat_id}/messages`);
      setMessages(msgRes.data.messages);

    } catch (err) {
      if (err.response?.status === 404) {
        setError('no_chat');
      } else {
        setError('error');
      }
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchChat();
  }, [fetchChat]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send typing indicator
  const sendTypingIndicator = useCallback((isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: isTyping ? 'typing_start' : 'typing_stop',
        user_id: user?.user_id,
        user_name: user?.company_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
        user_type: userType
      }));
    }
  }, [user, userType]);

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Send typing start
    if (value.length > 0) {
      sendTypingIndicator(true);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(false);
      }, 2000);
    } else {
      sendTypingIndicator(false);
    }
  };

  // WebSocket connection
  useEffect(() => {
    if (!chat?.chat_id) return;

    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/chat/${chat.chat_id}`;
    
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('Chat WebSocket connected');
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Handle new message
            if (data.type === 'new_message' && data.message) {
              setMessages(prev => {
                // Avoid duplicates
                if (prev.some(m => m.message_id === data.message.message_id)) {
                  return prev;
                }
                return [...prev, data.message];
              });
              // Stop showing typing when message arrives
              setIsOtherTyping(false);
            }
            
            // Handle reaction updates
            if (data.type === 'reaction' || data.type === 'reaction_removed') {
              setMessages(prev => prev.map(m => 
                m.message_id === data.message_id 
                  ? { ...m, reactions: data.reactions }
                  : m
              ));
            }
            
            // Handle typing indicator
            if (data.type === 'typing_indicator') {
              // Only show if it's from the other party
              if (data.user_id !== user?.user_id) {
                setIsOtherTyping(data.is_typing);
                setOtherTypingName(data.user_name || '');
              }
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };

        ws.onclose = () => {
          console.log('Chat WebSocket closed, reconnecting...');
          setTimeout(connectWebSocket, 3000);
        };

        ws.onerror = (err) => {
          console.error('Chat WebSocket error:', err);
        };
      } catch (err) {
        console.error('Failed to connect WebSocket:', err);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [chat?.chat_id]);

  // Send message
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending) return;

    // Stop typing indicator
    sendTypingIndicator(false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    setSending(true);
    try {
      const response = await axios.post(`/chat/${chat.chat_id}/messages`, {
        text: newMessage.trim()
      });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (err) {
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setSending(false);
    }
  };

  // Handle file upload
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      toast.error('File troppo grande. Massimo 20MB.');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo file non supportato. Usa PDF, immagini o documenti Word.');
      return;
    }

    setUploading(true);
    try {
      // Upload to Cloudinary via backend
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`/chat/${chat.chat_id}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setMessages(prev => [...prev, response.data]);
      toast.success('Documento caricato');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Errore nel caricamento del file';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = msg.created_at?.split('T')[0] || 'unknown';
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  // No chat state (mandate not signed)
  if (error === 'no_chat') {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Chat non disponibile</h3>
        <p className="text-gray-500 text-sm">
          La chat sarà attivata dopo la firma del mandato con un agente.
        </p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 rounded-lg p-6 text-center">
        <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-600">Errore nel caricamento della chat</p>
        <Button variant="outline" onClick={fetchChat} className="mt-3">
          Riprova
        </Button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden ${compact ? 'h-[400px]' : 'h-[600px]'}`} data-testid="property-chat">
      {/* Chat Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-black/5 bg-[#F5F5F7]">
        <div className="flex items-center space-x-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-sm">
            {userType === 'seller' 
              ? <Building className="h-5 w-5 text-[#1D1D1F]" strokeWidth={1.5} />
              : <User className="h-5 w-5 text-[#1D1D1F]" strokeWidth={1.5} />
            }
          </div>
          <div>
            <h3 className="font-semibold text-[#1D1D1F] text-[15px]">
              {userType === 'seller' ? 'Comunicazione con l\'agente' : 'Comunicazione con il proprietario'}
            </h3>
            <p className="text-[13px] text-[#86868B]">
              {userType === 'seller' ? chat?.agent_name : chat?.seller_name}
            </p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 bg-white">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-[#F5F5F7] rounded-2xl flex items-center justify-center mb-4">
              <MessageSquare className="h-8 w-8 text-[#86868B]" strokeWidth={1.5} />
            </div>
            <p className="text-[#1D1D1F] font-medium">Nessun messaggio ancora</p>
            <p className="text-[#86868B] text-[13px] mt-1">Inizia la conversazione!</p>
          </div>
        ) : (
          <>
            {Object.entries(groupedMessages).map(([date, msgs]) => (
              <div key={date}>
                <DateSeparator date={date} />
                {msgs.map((msg) => (
                  <ChatMessage 
                    key={msg.message_id} 
                    message={msg}
                    isOwn={msg.sender_id === user?.user_id}
                    userType={userType}
                    onClassify={() => fetchChat()}
                    onReaction={handleReaction}
                    currentUserId={user?.user_id}
                  />
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
        
        {/* Typing Indicator */}
        {isOtherTyping && (
          <div className="flex items-center space-x-2 px-3 py-2">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-[#86868B] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-[#86868B] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-[#86868B] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-[12px] text-[#86868B] italic">
              {otherTypingName || (userType === 'seller' ? 'L\'agente' : 'Il proprietario')} sta scrivendo...
            </span>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-black/5 bg-[#F5F5F7]">
        {/* Voice Recording UI */}
        {isRecording ? (
          <div className="flex items-center justify-between px-4 py-3 bg-red-50 rounded-2xl">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-[14px] font-medium text-red-600">
                Registrazione... {formatRecordingTime(recordingTime)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={cancelRecording}
                className="p-2 rounded-xl hover:bg-red-100 text-red-600 transition-colors"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={stopRecording}
                className="p-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                <Square className="h-5 w-5" fill="currentColor" />
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={sendMessage} className="flex items-center space-x-2">
            {/* File upload button */}
            <input 
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="p-2.5 rounded-xl text-[#86868B] hover:text-[#1D1D1F] hover:bg-white transition-colors"
              title="Carica documento"
            >
              {uploading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Paperclip className="h-5 w-5" strokeWidth={1.5} />
              )}
            </button>
            
            {/* Voice message button */}
            <button
              type="button"
              onClick={startRecording}
              className="p-2.5 rounded-xl text-[#86868B] hover:text-[#1D1D1F] hover:bg-white transition-colors"
              title="Messaggio vocale"
            >
              <Mic className="h-5 w-5" strokeWidth={1.5} />
            </button>
            
            {/* Message input */}
            <div className="flex-1 relative">
              <input
                value={newMessage}
                onChange={handleInputChange}
                placeholder="Scrivi un messaggio..."
                className="input-apple py-2.5"
                disabled={sending}
              />
            </div>
            
            {/* Send button */}
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="p-2.5 rounded-xl bg-[#1D1D1F] text-white hover:bg-[#000] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" strokeWidth={1.5} />
              )}
            </button>
          </form>
        )}
      </div>
        
    </div>
  );
};

// Chat list for sidebar/messages page
export const ChatList = ({ onSelectChat }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChats();
  }, []);

  const fetchChats = async () => {
    try {
      const response = await axios.get('/chat/my-chats');
      setChats(response.data);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className="text-center py-8">
        <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Nessuna conversazione attiva</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {chats.map((chat) => (
        <button
          key={chat.chat_id}
          onClick={() => onSelectChat(chat)}
          className="w-full p-4 flex items-center space-x-3 hover:bg-gray-50 transition-colors text-left"
        >
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Building className="h-6 w-6 text-gray-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="font-medium text-gray-900 truncate">
                {chat.property?.title || 'Immobile'}
              </p>
              {chat.last_message_at && (
                <span className="text-xs text-gray-400">
                  {format(parseISO(chat.last_message_at), 'HH:mm', { locale: it })}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 truncate">
              {chat.last_message_preview || 'Nessun messaggio'}
            </p>
          </div>
          {(chat.unread_seller > 0 || chat.unread_agent > 0) && (
            <Badge className="bg-[#0071e3] text-white">
              {chat.unread_seller || chat.unread_agent}
            </Badge>
          )}
        </button>
      ))}
    </div>
  );
};

export default PropertyChat;
